import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Chip,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
  Stack,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Lock as LockIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../../hooks/useAuthenticatedFetch';
import {
  DeliverableTemplate,
  TemplateSection,
  TemplateControl,
} from '@horwath-htl/shared-types';

interface SectionsStepProps {
  templateId: string;
  selectedSections: string[];
  onSectionsChange: (sections: string[]) => void;
}

interface SectionWithControls extends TemplateSection {
  controls: TemplateControl[];
}

/**
 * SectionsStep Component
 *
 * Step 2 of the DeliverableBuilder workflow.
 * Allows users to select which sections to include in their document.
 */
export const SectionsStep: React.FC<SectionsStepProps> = ({
  templateId,
  selectedSections,
  onSectionsChange,
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Fetch template details
  const {
    data: templateData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['template', templateId],
    queryFn: async () => {
      const response = await authenticatedFetch(`/api/deliverables/templates/${templateId}`);
      if (!response?.ok) {
        throw new Error('Failed to fetch template details');
      }
      return await response.json();
    },
    enabled: !!templateId,
  });

  const template: DeliverableTemplate | undefined = templateData?.template;

  // Parse sections and controls from template config
  const sectionsWithControls = useMemo((): SectionWithControls[] => {
    if (!template) return [];

    const sections = template.sections_config?.sections || [];
    const controls = template.controls_config?.controls || [];

    // Group controls by section_id
    const controlsBySection = controls.reduce<Record<string, TemplateControl[]>>((acc, control) => {
      const sectionId = control.section_id || 'uncategorized';
      if (!acc[sectionId]) {
        acc[sectionId] = [];
      }
      acc[sectionId].push(control);
      return acc;
    }, {});

    // Combine sections with their controls
    const result = sections
      .map((section) => ({
        ...section,
        controls: controlsBySection[section.id] || [],
      }))
      .sort((a, b) => a.order - b.order);

    // Add uncategorized section if there are controls without a section
    if (controlsBySection['uncategorized']?.length > 0) {
      result.push({
        id: 'uncategorized',
        name: 'Other Controls',
        required: false,
        description: 'Controls not assigned to a specific section',
        order: result.length,
        controls: controlsBySection['uncategorized'],
      });
    }

    return result;
  }, [template]);

  // Filter sections based on search term
  const filteredSections = useMemo(() => {
    if (!searchTerm.trim()) return sectionsWithControls;

    const lowerSearch = searchTerm.toLowerCase();
    return sectionsWithControls.filter(
      (section) =>
        section.name.toLowerCase().includes(lowerSearch) ||
        section.description?.toLowerCase().includes(lowerSearch) ||
        section.controls.some((control) =>
          control.name.toLowerCase().includes(lowerSearch)
        )
    );
  }, [sectionsWithControls, searchTerm]);

  // Get required sections that must always be selected
  const requiredSectionIds = useMemo(() => {
    return sectionsWithControls
      .filter((section) => section.required)
      .map((section) => section.id);
  }, [sectionsWithControls]);

  // Check if all optional sections are selected
  const allOptionalSelected = useMemo(() => {
    const optionalSections = sectionsWithControls.filter((s) => !s.required);
    return optionalSections.every((s) => selectedSections.includes(s.id));
  }, [sectionsWithControls, selectedSections]);

  // Handle section toggle
  const handleSectionToggle = (sectionId: string, isRequired: boolean) => {
    if (isRequired) return; // Cannot uncheck required sections

    if (selectedSections.includes(sectionId)) {
      onSectionsChange(selectedSections.filter((id) => id !== sectionId));
    } else {
      onSectionsChange([...selectedSections, sectionId]);
    }
  };

  // Select all sections
  const handleSelectAll = () => {
    const allSectionIds = sectionsWithControls.map((s) => s.id);
    onSectionsChange(allSectionIds);
  };

  // Deselect all optional sections (keep required ones)
  const handleDeselectAll = () => {
    onSectionsChange(requiredSectionIds);
  };

  // Toggle section expansion
  const toggleSectionExpansion = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  // Calculate selected count
  const selectedCount = selectedSections.length;
  const totalCount = sectionsWithControls.length;

  // Validation: show warning if no sections selected
  const showNoSectionsWarning = selectedSections.length === 0 && !isLoading;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load template details. Please try again.
      </Alert>
    );
  }

  if (!template) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Template not found or access denied.
      </Alert>
    );
  }

  if (sectionsWithControls.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        This template has no defined sections. You may proceed to the next step.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header with stats and actions */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            Select Sections
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose which sections to include in your document.
            {requiredSectionIds.length > 0 && (
              <> Required sections are marked with a lock icon.</>
            )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip
            label={`${selectedCount} / ${totalCount} selected`}
            color={selectedCount === totalCount ? 'success' : 'default'}
            variant="outlined"
          />
        </Stack>
      </Stack>

      {/* Search and bulk actions */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <TextField
            placeholder="Search sections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SelectAllIcon />}
              onClick={handleSelectAll}
              disabled={allOptionalSelected && requiredSectionIds.length === selectedSections.length - (sectionsWithControls.length - requiredSectionIds.length)}
            >
              Select All
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DeselectIcon />}
              onClick={handleDeselectAll}
              disabled={selectedSections.length === requiredSectionIds.length}
            >
              Deselect All
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Warning if no sections selected */}
      {showNoSectionsWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No sections are selected. Please select at least one section to proceed.
        </Alert>
      )}

      {/* Sections list */}
      <Paper variant="outlined">
        <List disablePadding>
          {filteredSections.map((section, index) => {
            const isSelected = selectedSections.includes(section.id);
            const isExpanded = expandedSections.has(section.id);
            const controlCount = section.controls.length;

            return (
              <React.Fragment key={section.id}>
                {index > 0 && <Divider />}
                <ListItem
                  sx={{
                    bgcolor: isSelected ? 'action.selected' : 'transparent',
                    '&:hover': {
                      bgcolor: section.required
                        ? 'action.selected'
                        : 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Checkbox
                      edge="start"
                      checked={isSelected}
                      onChange={() => handleSectionToggle(section.id, section.required)}
                      disabled={section.required}
                      inputProps={{
                        'aria-label': `Select ${section.name}`,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="subtitle1"
                          component="span"
                          sx={{
                            fontWeight: section.required ? 600 : 400,
                          }}
                        >
                          {section.name}
                        </Typography>
                        {section.required && (
                          <Tooltip title="This section is required and cannot be removed">
                            <LockIcon fontSize="small" color="action" />
                          </Tooltip>
                        )}
                      </Stack>
                    }
                    secondary={
                      <Stack spacing={0.5} component="span">
                        {section.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="span"
                            sx={{ display: 'block' }}
                          >
                            {section.description}
                          </Typography>
                        )}
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          component="span"
                        >
                          <Chip
                            size="small"
                            label={`${controlCount} control${controlCount !== 1 ? 's' : ''}`}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.75rem' }}
                          />
                          {section.required && (
                            <Chip
                              size="small"
                              label="Required"
                              color="primary"
                              sx={{ height: 20, fontSize: '0.75rem' }}
                            />
                          )}
                        </Stack>
                      </Stack>
                    }
                  />
                  <ListItemSecondaryAction>
                    {controlCount > 0 && (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => toggleSectionExpansion(section.id)}
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>

                {/* Expandable controls list */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ pl: 8, pr: 2, pb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      Controls in this section:
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {section.controls.map((control) => (
                        <Tooltip
                          key={control.id}
                          title={
                            <Box>
                              <Typography variant="body2">
                                <strong>Type:</strong> {control.type}
                              </Typography>
                              {control.description && (
                                <Typography variant="body2">
                                  <strong>Description:</strong> {control.description}
                                </Typography>
                              )}
                              {control.auto_mapped && (
                                <Typography variant="body2">
                                  <strong>Auto-mapped:</strong> Yes
                                  {control.data_source && ` (${control.data_source})`}
                                </Typography>
                              )}
                            </Box>
                          }
                        >
                          <Chip
                            size="small"
                            label={control.name}
                            variant="outlined"
                            icon={control.required ? <InfoIcon fontSize="small" /> : undefined}
                            sx={{
                              fontSize: '0.75rem',
                              bgcolor: control.auto_mapped ? 'info.50' : undefined,
                            }}
                          />
                        </Tooltip>
                      ))}
                    </Stack>
                  </Box>
                </Collapse>
              </React.Fragment>
            );
          })}
        </List>
      </Paper>

      {/* No results message */}
      {filteredSections.length === 0 && searchTerm && (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">
            No sections found matching &quot;{searchTerm}&quot;
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SectionsStep;
